import {
  PolymorphicForwardedRef,
  PolymorphicProps,
} from '@axa-ch/react-polymorphic-types';
import { PropsWithChildren } from 'react';

import { cn } from '~/lib/utils/classname';

type Slots = 'main' | 'inner';

export const defaultElement: React.ElementType = 'div' as const;
export type AllowedElements =
  | typeof defaultElement
  | keyof React.JSX.IntrinsicElements;

export const defaultInnerElement: React.ElementType = 'div' as const;
export type AllowedInnerElements =
  | typeof defaultInnerElement
  | keyof React.JSX.IntrinsicElements;

export type OwnProps<
  E extends AllowedElements,
  I extends AllowedInnerElements,
> = React.ComponentPropsWithoutRef<E> & {
  innerAs?: I;
  className?: string;
  classNames?: Partial<Record<Slots, string>>;

  ref?: PolymorphicForwardedRef<E>;
} & PropsWithChildren &
  React.JSX.IntrinsicAttributes;

export type Props<
  T extends AllowedElements,
  I extends AllowedInnerElements,
> = PolymorphicProps<OwnProps<T, I>, T, AllowedElements>;

const LayoutGrid = <
  E extends AllowedElements = typeof defaultElement,
  I extends AllowedInnerElements = typeof defaultInnerElement,
>({
  as: asComponent,
  innerAs: innerAsComponent,
  className,
  classNames,
  children,
  ...rest
}: Props<E, I>) => {
  const Component = asComponent || defaultElement;
  const InnerComponent = innerAsComponent || defaultInnerElement;

  return (
    <Component
      className={cn(
        'max-w-screen px-(--layout-grid-padding--lg) max-lg:px-(--layout-grid-padding)',
        className,
        classNames?.main
      )}
      {...rest}
    >
      <InnerComponent
        className={cn(
          'relative mx-auto grid w-full max-w-(--layout-grid-max-width) grid-cols-12 gap-x-10 max-lg:grid-cols-2',
          classNames?.inner
        )}
      >
        {children}
      </InnerComponent>
    </Component>
  );
};

export default LayoutGrid;
